import { Check, Hourglass, MessageSquare, Minus, Search } from 'lucide-react';
import type { Personne } from '../../../types';
import { heureCourte, horaireCourt } from '../horaire';
import { jourLong } from './dates';
import { PASTILLE_ATTR, pastilleId } from './constantes';
import { statut, lireChoix, type ChoixEtat, type EtatSeance, type Seance } from './seancesDemande';
import { LIBELLE_ETAT } from './textesDemande';

/** La couleur du pôle d'une séance (celle de sa pastille), ou null. */
export const couleurDuPole = (meta: { poles: { id: number; couleur: string | null }[] } | null | undefined, poleId: number | null): string | null =>
  (poleId == null ? null : (meta?.poles ?? []).find((p) => p.id === poleId)?.couleur ?? null);

const ICONE: Record<EtatSeance, typeof Check> = { rech: Search, trouve: Check, deja: Hourglass, off: Minus };
export const IconeEtat = ({ etat, size = 14 }: { etat: EtatSeance; size?: number }) => { const I = ICONE[etat]; return <I size={size} aria-hidden="true" />; };

/** Ce que dit la pastille sur l'état de la séance : « À rechercher », le nom de la personne, « Déjà demandé »… */
export function detailSeance(s: Seance, e: ChoixEtat, personnes: Record<number, Personne>): { etat: EtatSeance; texte: string; messages: number; manqueGarant: boolean } {
  const etat = statut(s, e);
  const c = lireChoix(e, s.cle);
  const p = c.remp != null ? personnes[c.remp] : undefined;
  if (etat === 'trouve') {
    const manqueGarant = !!p?.is_stagiaire && c.garant == null;
    return { etat, texte: `${p?.display_name ?? '—'}${p?.is_stagiaire ? ' (stagiaire)' : ''}${manqueGarant ? ' · garant·e à choisir' : ''}`, messages: 0, manqueGarant };
  }
  if (etat === 'rech') return { etat, texte: LIBELLE_ETAT.rech, messages: c.prevenir.length, manqueGarant: false };
  // Un brouillon repéré dans Tipee n'est pas « rien à refaire » : il attend l'envoi de la personne (depuis l'aperçu).
  return { etat, texte: s.aEnvoyer ? 'À envoyer' : LIBELLE_ETAT[etat], messages: 0, manqueGarant: false };
}

interface Props {
  seance: Seance;
  e: ChoixEtat;
  personnes: Record<number, Personne>;
  couleurPole: string | null;
  /** 'c' : dans le calendrier ; 'p' : dans le panneau (deux pastilles par séance, jamais le même identifiant). */
  ou: 'c' | 'p';
  onOuvrir: (s: Seance, ou: 'c' | 'p') => void;
  /** Une seule ligne (icône de statut + info-bulle) quand la case est basse. */
  compacte?: boolean;
  /** La fenêtre de cette séance est ouverte. */
  ouverte?: boolean;
}

export function PastilleSeance({ seance: s, e, personnes, couleurPole, ou, onOuvrir, compacte, ouverte }: Props) {
  const d = detailSeance(s, e, personnes);
  const horaire = horaireCourt(s.debut, s.fin);
  const messages = d.messages ? ` · ${d.messages} message${d.messages > 1 ? 's' : ''} personnel${d.messages > 1 ? 's' : ''}` : '';
  // Le nom commence par ce qu'on lit sur la pastille (« AT Debout 10 h ») : une commande vocale la trouve ; le jour vient ensuite.
  const libelle = `${s.atelierNom} ${horaire || heureCourte(s.debut)}, ${jourLong(s.date)} : ${d.texte}${messages} — ${d.etat === 'deja' ? (s.aEnvoyer ? "ouvrir pour l'envoyer" : 'voir la demande') : 'modifier'}`;
  return (
    <button
      type="button" className={`nd-pastille nd-st-${d.etat}${d.manqueGarant ? ' nd-manque' : ''}${compacte ? ' nd-pastille--compacte' : ''}`}
      {...{ [PASTILLE_ATTR]: pastilleId(ou, s.cle) }}
      style={{ ['--pole' as string]: couleurPole ?? 'var(--ink-3)' }}
      aria-label={libelle} aria-haspopup="dialog" aria-expanded={ouverte ? true : undefined} title={compacte ? `${d.texte}${messages}` : undefined}
      onClick={(ev) => { ev.stopPropagation(); onOuvrir(s, ou); }}
      onPointerDown={(ev) => ev.stopPropagation()}
    >
      <span className="nd-pastille-l1">
        <span className="nd-dot" aria-hidden="true" />
        <span className="nd-pastille-nom">{s.atelierNom}</span>
        <span className="nd-pastille-ico"><IconeEtat etat={d.etat} /></span>
        <span className="nd-pastille-h">{heureCourte(s.debut)}</span>
      </span>
      <span className="nd-pastille-l2">
        <IconeEtat etat={d.etat} />
        <span>{d.texte}</span>
        {d.messages > 0 && <span className="nd-msg" aria-hidden="true" title="Message personnel"><MessageSquare size={12} />{d.messages}</span>}
      </span>
    </button>
  );
}
